import { KitTypeAndDescriptions } from '@csp/csp-common-hbs-model';
import { VisitHbsKitConfigMapper } from '@csp/csp-common-hbs/src/lib/mapper/VisitHbsKitConfigMapper';
import { Maybe, Patient, VisitVariantType } from '@csp/csp-common-model';
import { ScheduleVersionRequestRef } from '@csp/csp-common-scheduling';
import { isDefined } from '@csp/csp-common-util';
import { WebHcpPatientVisitEntry } from '@csp/csp-web-content-definitions';
import { WindowInfo } from '@csp/csp-common-date-util';
import { PatientVisitTextsMapper } from '../mapper/PatientVisitTextsMapper';
import { GeneratedVisit } from '../schedule/model/GeneratedVisit';
import { ScheduleVisitsContent } from './ScheduleVisitsContent';
import { ScheduledVisitUniqueId } from './ScheduledVisitUniqueId';
import { UnbookedVisit } from './UnbookedVisit';

const extractVisitUniqueId = (requestRef: ScheduleVersionRequestRef): Maybe<ScheduledVisitUniqueId> =>
  ScheduleVersionRequestRef.toKey(requestRef);

const from = (
  generatedVisit: GeneratedVisit,
  patient: Patient,
  patientVisitTexts: WebHcpPatientVisitEntry,
  scheduleCms: ScheduleVisitsContent,
  allKitsWithDesc: Maybe<KitTypeAndDescriptions>,
): UnbookedVisit => {
  const uniqueId = (generatedVisit?.requestRef && extractVisitUniqueId(generatedVisit.requestRef)) ?? '';

  const canBook =
    generatedVisit.request.variant === VisitVariantType.CLOSE_OUT ? patient.canBookCloseoutVisit : patient.canBookVisit;

  const visitTexts = PatientVisitTextsMapper.fromGeneratedVisit(generatedVisit, patientVisitTexts, scheduleCms);

  const hbsKitConfig = VisitHbsKitConfigMapper.from(
    generatedVisit.request.hbs,
    allKitsWithDesc?.items || [],
    visitTexts.bioSampleKitDescriptions?.map(text => text.sample_kit?.description).filter(isDefined),
  );

  const hbsAnalyteTypes =
    visitTexts.bioSampleAnalyteTypes?.map(analyteType => analyteType.analyte_name).filter(isDefined) ?? [];

  return {
    ...generatedVisit,
    visitTexts,
    canBook,
    window: WindowInfo.from(generatedVisit.requestRef?.windowStart, generatedVisit.requestRef?.windowEnd),
    uniqueId,
    isUnscheduledVisit: generatedVisit.request.variant === VisitVariantType.UNSCHEDULED,
    hbsKitConfig,
    hbsAnalyteTypes,
  };
};

export const UnbookedVisitMapper = {
  from,
  /** extractVisitUniqueId export only for test purpose **/
  extractVisitUniqueId,
};
